/**
 * api.ts — the typed HTTP client over `personal/shared-types.ts`.
 *
 * Contract rules implemented here (docs/api-personal.md §1):
 *  - base path `/api`, JSON in/out, credentials from the session cookie (never a token);
 *  - every mutation carries `X-Requested-With: fetch` (CSRF, §1.2);
 *  - path params are `encodeURIComponent`-ed (Financy account ids may contain `=`);
 *  - `401 UNAUTHENTICATED` calls `onUnauthenticated()` — the app routes to /login keeping
 *    the intended route;
 *  - any non-2xx JSON body `{error:{code,message}}` becomes an `ApiError`, which the UI
 *    surfaces as a toast (Hebrew message from the server, or `i18n/he.ts` as a fallback).
 *
 * The generic `call()` is keyed by `ApiRouteKey`, so renaming a route in the contract fails
 * the typecheck here and in `api.mock.ts` (api-personal.md §1.6).
 */

import type {
  Account,
  Alert,
  AlertsQuery,
  AlertsResponse,
  ApiRouteKey,
  ApiRoutes,
  CardDetailResponse,
  CardsResponse,
  CategoriesResponse,
  CategoryDetailResponse,
  CategorySettings,
  ChangePasswordRequest,
  Connection,
  CreateManualEntryRequest,
  ErrorCode,
  GroupKey,
  LoansQuery,
  LoansResponse,
  LoginRequest,
  LoginResponse,
  ManualEntriesResponse,
  ManualEntry,
  MeResponse,
  NetWorthResponse,
  OkResponse,
  PeriodParam,
  ReadAllAlertsRequest,
  ReadAllAlertsResponse,
  SettingsResponse,
  SseEvent,
  SummaryResponse,
  SyncRequestResponse,
  SyncStatusResponse,
  TransactionDetail,
  TransactionsQuery,
  TransactionsResponse,
  UpdateAccountRequest,
  UpdateAlertRequest,
  UpdateCardRequest,
  UpdateCategoryRequest,
  UpdateSettingsRequest,
  UpdateTransactionRequest,
  UpdateTransactionResponse,
  UsersResponse,
} from '../../shared-types.ts';
import { he } from './i18n/he.ts';

// ───────────────────────────── errors ─────────────────────────────

/** A failed API call. `code` is the contract's `ErrorCode`, or `NETWORK` when fetch itself failed. */
export class ApiError extends Error {
  code: ErrorCode | 'NETWORK';
  status: number;
  details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode | 'NETWORK', message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/** Hebrew message for an error, preferring the server's own text. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message || he.errors[err.code] || he.errors.INTERNAL || '';
  if (err instanceof Error && err.message) return err.message;
  return he.errors.INTERNAL ?? '';
}

// ───────────────────────────── route typing ─────────────────────────────

type MethodOf<K extends string> = K extends `${infer M} ${string}` ? M : never;
type PathOf<K extends string> = K extends `${string} ${infer P}` ? P : never;
type ResOf<K extends ApiRouteKey> = ApiRoutes[K]['res'];
type BodyOf<K extends ApiRouteKey> = ApiRoutes[K] extends { body?: infer B } ? B : undefined;
type QueryOf<K extends ApiRouteKey> = ApiRoutes[K] extends { query: infer Q } ? Q : undefined;

export interface CallOptions<K extends ApiRouteKey> {
  /** Values for the `:name` segments of the route path. */
  params?: Record<string, string>;
  query?: QueryOf<K>;
  body?: BodyOf<K>;
  signal?: AbortSignal;
}

function fillPath(path: string, params: Record<string, string> | undefined): string {
  return path.replace(/:([A-Za-z_]+)/g, (_m, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new ApiError('VALIDATION', `missing path param ${name}`, 0);
    return encodeURIComponent(value);
  });
}

function queryString(query: unknown): string {
  if (!query || typeof query !== 'object') return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export interface ApiOptions {
  /** Defaults to `''` (same origin). */
  baseUrl?: string;
  /** Called on `401 UNAUTHENTICATED` before the error is thrown. */
  onUnauthenticated?: () => void;
  /** Injected for tests. */
  fetchImpl?: typeof fetch;
}

// ───────────────────────────── the client surface ─────────────────────────────

/** Everything the screens may call. `api.mock.ts` implements the same interface. */
export interface Api {
  login(body: LoginRequest): Promise<LoginResponse>;
  logout(): Promise<OkResponse>;
  me(): Promise<MeResponse>;
  changePassword(body: ChangePasswordRequest): Promise<OkResponse>;

  summary(period?: PeriodParam): Promise<SummaryResponse>;

  transactions(query: TransactionsQuery): Promise<TransactionsResponse>;
  transaction(id: string): Promise<TransactionDetail>;
  updateTransaction(id: string, body: UpdateTransactionRequest): Promise<UpdateTransactionResponse>;

  categories(period?: PeriodParam): Promise<CategoriesResponse>;
  category(key: GroupKey, period?: PeriodParam): Promise<CategoryDetailResponse>;
  updateCategory(key: GroupKey, body: UpdateCategoryRequest): Promise<CategorySettings>;

  cards(): Promise<CardsResponse>;
  card(id: string): Promise<CardDetailResponse>;
  updateCard(id: string, body: UpdateCardRequest): Promise<CardDetailResponse>;

  netWorth(): Promise<NetWorthResponse>;

  /** REDESIGN §6 — the loans screen. `sort` echoes back on the response. */
  loans(query?: LoansQuery): Promise<LoansResponse>;

  accounts(): Promise<{ items: Account[] }>;
  updateAccount(id: string, body: UpdateAccountRequest): Promise<Account>;
  connections(): Promise<{ items: Connection[] }>;

  settings(): Promise<SettingsResponse>;
  updateSettings(body: UpdateSettingsRequest): Promise<SettingsResponse>;
  users(): Promise<UsersResponse>;

  sync(providerRefresh?: boolean): Promise<SyncRequestResponse>;
  syncStatus(): Promise<SyncStatusResponse>;

  alerts(query?: AlertsQuery): Promise<AlertsResponse>;
  updateAlert(id: string, body: UpdateAlertRequest): Promise<Alert>;
  readAllAlerts(body?: ReadAllAlertsRequest): Promise<ReadAllAlertsResponse>;

  manualEntries(): Promise<ManualEntriesResponse>;
  createManualEntry(body: CreateManualEntryRequest): Promise<ManualEntry>;
  deleteManualEntry(id: string): Promise<OkResponse>;

  /** SSE subscription; returns an unsubscribe function. */
  subscribe(onEvent: (event: SseEvent) => void): () => void;
  /** URL for the CSV export (a plain cookie-authenticated GET). */
  exportUrl(query: Record<string, string | number | undefined>): string;
  /** `true` for the mock adapter — the UI shows the demo pill on the login screen. */
  readonly isMock: boolean;
}

// ───────────────────────────── HTTP implementation ─────────────────────────────

export function createHttpApi(options: ApiOptions = {}): Api {
  const baseUrl = options.baseUrl ?? '';
  const doFetch = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  async function call<K extends ApiRouteKey>(key: K, opts: CallOptions<K> = {}): Promise<ResOf<K>> {
    const space = key.indexOf(' ');
    const method = key.slice(0, space) as MethodOf<K>;
    const path = key.slice(space + 1) as PathOf<K>;
    const url = baseUrl + fillPath(path, opts.params) + queryString(opts.query);

    const headers: Record<string, string> = { Accept: 'application/json' };
    const isMutation = method !== 'GET';
    if (isMutation) headers['X-Requested-With'] = 'fetch'; // CSRF (api-personal.md §1.2)
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json; charset=utf-8';

    let response: Response;
    try {
      response = await doFetch(url, {
        method,
        headers,
        credentials: 'same-origin',
        cache: 'no-store',
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError('NETWORK', he.errors.NETWORK ?? '', 0);
    }

    if (response.status === 401) {
      options.onUnauthenticated?.();
    }

    const text = await response.text();
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text) as unknown;
      } catch {
        payload = null;
      }
    }

    if (!response.ok) {
      const body = payload as { error?: { code?: ErrorCode; message?: string; details?: Record<string, unknown> } } | null;
      const code = body?.error?.code ?? 'INTERNAL';
      const message = body?.error?.message || he.errors[code] || (he.errors.INTERNAL ?? '');
      throw new ApiError(code, message, response.status, body?.error?.details);
    }
    return payload as ResOf<K>;
  }

  return {
    login: (body) => call('POST /api/auth/login', { body }),
    logout: () => call('POST /api/auth/logout', {}),
    me: () => call('GET /api/auth/me', {}),
    changePassword: (body) => call('POST /api/auth/password', { body }),

    summary: (period) => call('GET /api/summary', { query: period ? { period } : {} }),

    transactions: (query) => call('GET /api/transactions', { query }),
    transaction: (id) => call('GET /api/transactions/:id', { params: { id } }),
    updateTransaction: (id, body) => call('PATCH /api/transactions/:id', { params: { id }, body }),

    categories: (period) => call('GET /api/categories', { query: period ? { period } : {} }),
    category: (key, period) => call('GET /api/categories/:key', { params: { key }, query: period ? { period } : {} }),
    updateCategory: (key, body) => call('PATCH /api/categories/:key', { params: { key }, body }),

    cards: () => call('GET /api/cards', {}),
    card: (id) => call('GET /api/cards/:id', { params: { id } }),
    updateCard: (id, body) => call('PATCH /api/cards/:id', { params: { id }, body }),

    netWorth: () => call('GET /api/networth', {}),

    loans: (query) => call('GET /api/loans', { query: query ?? {} }),

    accounts: () => call('GET /api/accounts', {}),
    updateAccount: (id, body) => call('PATCH /api/accounts/:id', { params: { id }, body }),
    connections: () => call('GET /api/connections', {}),

    settings: () => call('GET /api/settings', {}),
    updateSettings: (body) => call('PATCH /api/settings', { body }),
    users: () => call('GET /api/users', {}),

    sync: (providerRefresh) => call('POST /api/sync', { query: providerRefresh ? { providerRefresh: '1' } : {} }),
    syncStatus: () => call('GET /api/sync/status', {}),

    alerts: (query) => call('GET /api/alerts', { query: query ?? {} }),
    updateAlert: (id, body) => call('PATCH /api/alerts/:id', { params: { id }, body }),
    readAllAlerts: (body) => call('POST /api/alerts/read-all', { body: body ?? {} }),

    manualEntries: () => call('GET /api/manual-entries', {}),
    createManualEntry: (body) => call('POST /api/manual-entries', { body }),
    deleteManualEntry: (id) => call('DELETE /api/manual-entries/:id', { params: { id } }),

    subscribe(onEvent) {
      if (typeof EventSource === 'undefined') return () => undefined;
      const source = new EventSource(`${baseUrl}/api/events`, { withCredentials: true });
      const names: Array<SseEvent['event']> = [
        'hello',
        'sync:start',
        'sync:progress',
        'sync:done',
        'sync:error',
        'alerts:changed',
        'data:changed',
      ];
      const handlers = names.map((name) => {
        const handler = (ev: MessageEvent<string>) => {
          try {
            onEvent({ event: name, data: JSON.parse(ev.data) } as SseEvent);
          } catch {
            /* a malformed frame is ignored — the next poll/refresh recovers */
          }
        };
        source.addEventListener(name, handler as EventListener);
        return { name, handler };
      });
      return () => {
        for (const { name, handler } of handlers) source.removeEventListener(name, handler as EventListener);
        source.close();
      };
    },

    exportUrl(query) {
      return `${baseUrl}/api/export/transactions.csv${queryString(query)}`;
    },

    isMock: false,
  };
}

/**
 * Which adapter to use: the mock is selected by `window.__API_MOCK__ === true` or by
 * `?mock=1` in the URL (`?mock=0` forces the real server). The flag is remembered in
 * `sessionStorage` so in-app navigation keeps it.
 */
export function shouldUseMock(): boolean {
  try {
    const flagged = (globalThis as { __API_MOCK__?: boolean }).__API_MOCK__;
    if (typeof flagged === 'boolean') return flagged;
    if (typeof location !== 'undefined') {
      const q = new URLSearchParams(location.search);
      const value = q.get('mock');
      if (value === '1' || value === 'true') {
        sessionStorage.setItem('pf.mock', '1');
        return true;
      }
      if (value === '0' || value === 'false') {
        sessionStorage.removeItem('pf.mock');
        return false;
      }
    }
    return sessionStorage.getItem('pf.mock') === '1';
  } catch {
    return false;
  }
}
